import {
  cellCenterX,
  cellCenterY,
  createField,
  type GridSpec,
  type ScalarField,
} from '../sim/grid.ts';
import type { Conditions, TerrainState } from '../sim/scene.ts';
import { STABILITY_MODEL } from '../sim/stability.ts';
import { elevationOfTemperature, snowFraction, temperatureAt } from '../sim/temperature.ts';
import type { OverlayMode } from '../state/store.ts';
import {
  depositColor,
  depositRampMax,
  netColor,
  netHalfRange,
  originColor,
  pressureColor,
  speedColor,
  speedRampMax,
  thicknessColor,
  thicknessRampMax,
} from './avalancheColors.ts';

/**
 * Per-vertex colouring for the terrain mesh, one function per overlay mode. Colours are linear
 * 0…1 RGB triples, written straight into the mesh's `color` BufferAttribute.
 *
 * The storm and stability overlays (`stormSnow`, `windLoading`, `windSpeed`, `shelter`,
 * `stability`, `naturalStability`, `problem`, `criticalDepth`, `propagation`) take their data as
 * an already-resampled `activeField` (terrain grid, from `state/store.ts`'s `activeOverlayField`)
 * rather than reaching into `SnowpackState` themselves — this module stays free of the storm/
 * stability result types and only cares about plain scalar fields. Cells with nothing to show
 * (no slab, no critical layer, …) are encoded as `NaN` in that field and rendered as neutral
 * terrain — see `maskBelow`.
 */

export type RGB = readonly [number, number, number];

const SEA: RGB = [0.16, 0.35, 0.55];
const VEG_LOW: RGB = [0.3, 0.45, 0.2];
const VEG_HIGH: RGB = [0.45, 0.4, 0.28];
const ROCK: RGB = [0.5, 0.48, 0.46];
const SNOW_NATURAL: RGB = [0.9, 0.94, 1.0];

const SLOPE_NEUTRAL: RGB = [0.55, 0.55, 0.52];
/**
 * "No data here" in every data overlay (no storm, no slab, too flat, …). A plain grey, not the
 * natural terrain colours, which include a vegetation green easily mistaken for "good".
 */
const OVERLAY_NEUTRAL: RGB = [0.62, 0.62, 0.62];
const SLOPE_27: RGB = [0.95, 0.85, 0.2];
const SLOPE_30: RGB = [0.95, 0.55, 0.15];
const SLOPE_35: RGB = [0.85, 0.15, 0.15];
const SLOPE_40: RGB = [0.55, 0.15, 0.75];
const SLOPE_45: RGB = [0.08, 0.05, 0.08];

/** Sequential snow-depth ramp stops, evenly spaced from 0 to the ramp max. */
const SNOWDEPTH_STOPS: RGB[] = [
  [0.55, 0.55, 0.55],
  [0.75, 0.85, 0.95],
  [0.25, 0.45, 0.75],
  [0.3, 0.1, 0.55],
];

/** Sequential storm-snow (net new snow) ramp stops: grey (none) through greens. */
const STORMSNOW_STOPS: RGB[] = [
  [0.55, 0.55, 0.55],
  [0.75, 0.85, 0.65],
  [0.25, 0.6, 0.35],
  [0.05, 0.3, 0.15],
];

/** Sequential wind-speed ramp stops: pale calm through dark red for violent. */
const WINDSPEED_STOPS: RGB[] = [
  [0.82, 0.86, 0.78],
  [0.95, 0.78, 0.25],
  [0.85, 0.35, 0.12],
  [0.4, 0.05, 0.05],
];

const TEMP_COLD: RGB = [0.15, 0.35, 0.75];
const TEMP_WARM: RGB = [0.85, 0.2, 0.15];
const TEMP_ZERO: RGB = [1, 1, 1];

const PHASE_RAIN: RGB = [0.25, 0.55, 0.25];
const PHASE_MIXED: RGB = [0.55, 0.35, 0.75];
const PHASE_SNOW: RGB = [0.85, 0.92, 1.0];

/** Diverging wind-loading colours: red = scoured, blue = loaded. */
const SCOUR: RGB = [0.85, 0.2, 0.15];
const LOAD: RGB = [0.2, 0.35, 0.85];

/** Diverging shelter (Sx) colours: orange = exposed, blue = sheltered. */
const EXPOSED: RGB = [0.85, 0.45, 0.1];
const SHELTERED: RGB = [0.15, 0.45, 0.85];

/** Fixed half-range (deg) for the shelter overlay — Sx rarely exceeds this either way. */
export const SHELTER_HALF_RANGE_DEG = 30;

/** SK38 stability classes (Jamieson & Johnston): poor / fair / good. */
const STABILITY_POOR: RGB = [0.85, 0.15, 0.15];
const STABILITY_FAIR: RGB = [0.95, 0.55, 0.15];
const STABILITY_GOOD: RGB = [0.25, 0.65, 0.25];

/** Below this slope (deg) a cell is never avalanche terrain, whatever the stability index says. */
export const STABILITY_NEUTRAL_SLOPE_DEG = 25;

/** Categorical colours for the four EAWS-ish avalanche problems, indexed by `AvalancheProblem − 1`
 * (`None` renders as neutral terrain instead). */
const PROBLEM_COLORS: RGB[] = [
  [0.31, 0.65, 0.88], // New snow
  [0.88, 0.64, 0.25], // Wind slab
  [0.55, 0.24, 0.75], // Persistent weak layer
  [0.18, 0.62, 0.56], // Wet snow
];

/** Sequential critical-depth ramp stops: grey (none) through teal. */
const CRITICALDEPTH_STOPS: RGB[] = [
  [0.55, 0.55, 0.55],
  [0.65, 0.85, 0.8],
  [0.15, 0.55, 0.5],
  [0.05, 0.3, 0.28],
];

/** Crown cover ramp stops: pale to dark green. */
const FOREST_STOPS: RGB[] = [
  [0.85, 0.93, 0.82],
  [0.45, 0.75, 0.45],
  [0.1, 0.5, 0.2],
  [0.0, 0.25, 0.08],
];

/** Sequential propagation-propensity ramp stops: grey through yellow to red. */
const PROPAGATION_STOPS: RGB[] = [
  [0.55, 0.55, 0.55],
  [0.95, 0.85, 0.2],
  [0.9, 0.4, 0.15],
  [0.6, 0.05, 0.05],
];

function stabilityClassColor(value: number): RGB {
  if (value < STABILITY_MODEL.poor) return STABILITY_POOR;
  if (value < STABILITY_MODEL.fair) return STABILITY_FAIR;
  return STABILITY_GOOD;
}

/**
 * Mask a continuous field to `NaN` wherever `mask` is at or below `minValue` — used so cells with
 * no slab, or no critical layer, render as neutral terrain instead of a spurious class/colour.
 * `mask` may be `value` itself (masking a field against its own "nothing here" sentinel of 0).
 */
export function maskBelow(value: ScalarField, mask: ScalarField, minValue: number): ScalarField {
  const out = createField(value.spec);
  for (let i = 0; i < value.data.length; i++) {
    out.data[i] = mask.data[i]! > minValue ? value.data[i]! : NaN;
  }
  return out;
}

/**
 * Nearest-cell (not bilinear) resample of a categorical field onto another grid — used for the
 * avalanche-problem overlay, where interpolating between classes would invent nonexistent ones.
 */
export function nearestResampleCategorical(
  data: Uint8Array,
  from: GridSpec,
  to: GridSpec,
): ScalarField {
  const out = createField(to);
  for (let r = 0; r < to.rows; r++) {
    const y = cellCenterY(to, r);
    const rowSrc = Math.min(
      from.rows - 1,
      Math.max(0, Math.floor((from.maxY - y) / from.cellSize)),
    );
    for (let c = 0; c < to.cols; c++) {
      const x = cellCenterX(to, c);
      const colSrc = Math.min(
        from.cols - 1,
        Math.max(0, Math.floor((x - from.minX) / from.cellSize)),
      );
      out.data[r * to.cols + c] = data[rowSrc * from.cols + colSrc]!;
    }
  }
  return out;
}

/** Ramp max (m) for the critical-depth overlay: at least 30 cm, or the observed max, ignoring
 * cells with no critical layer (`NaN`). */
export function criticalDepthRampMax(field: ScalarField): number {
  let max = 0;
  for (const v of field.data) if (!Number.isNaN(v) && v > max) max = v;
  return Math.max(0.3, max);
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function mix(a: RGB, b: RGB, t: number): RGB {
  const c = clamp01(t);
  return [lerp(a[0], b[0], c), lerp(a[1], b[1], c), lerp(a[2], b[2], c)];
}

export function bareGroundColor(elevation: number, slope: number): RGB {
  if (elevation <= 1) return SEA;
  const vegT = clamp01(elevation / 1400);
  const veg = mix(VEG_LOW, VEG_HIGH, vegT);
  const rockT = clamp01((slope - 35) / 10);
  return mix(veg, ROCK, rockT);
}

function naturalColor(elevation: number, slope: number, snowDepth: number): RGB {
  const ground = bareGroundColor(elevation, slope);
  const snowT = clamp01((snowDepth - 0.05) / 0.15);
  return mix(ground, SNOW_NATURAL, snowT);
}

function slopeColor(slope: number): RGB {
  if (slope < 27) return SLOPE_NEUTRAL;
  if (slope < 30) return SLOPE_27;
  if (slope < 35) return SLOPE_30;
  if (slope < 40) return SLOPE_35;
  if (slope < 45) return SLOPE_40;
  return SLOPE_45;
}

/** Ramp max for the snow-depth overlay: at least 3 m, or the observed max if deeper. */
export function snowDepthRampMax(snowDepth: ScalarField): number {
  let max = 0;
  for (const v of snowDepth.data) if (v > max) max = v;
  return Math.max(3, max);
}

/** Generic sequential ramp: `value` 0…`max` mapped across evenly spaced `stops`. */
function sequentialColor(value: number, max: number, stops: RGB[]): RGB {
  const steps = stops.length - 1;
  const t = clamp01(max > 0 ? value / max : 0) * steps;
  const i0 = Math.min(steps - 1, Math.floor(t));
  const stop0 = stops[i0]!;
  const stop1 = stops[i0 + 1]!;
  return mix(stop0, stop1, t - i0);
}

/** Generic diverging ramp: 0 = white, ±half = fully saturated `neg`/`pos`. */
function divergingColor(value: number, half: number, neg: RGB, pos: RGB): RGB {
  const t = clamp01(Math.abs(value) / Math.max(1e-9, half));
  return value >= 0 ? mix(TEMP_ZERO, pos, t) : mix(TEMP_ZERO, neg, t);
}

function snowDepthColor(depth: number, max: number): RGB {
  return sequentialColor(depth, max, SNOWDEPTH_STOPS);
}

/** Ramp max (m) for the storm-snow overlay: at least 10 cm, or the observed max net gain. */
export function stormSnowRampMax(field: ScalarField): number {
  let max = 0;
  for (const v of field.data) if (v > max) max = v;
  return Math.max(0.1, max);
}

/** Half-range (m) for the wind-loading overlay: the 98th percentile of |change|, min 10 cm. */
export function windLoadingHalfRangeM(field: ScalarField): number {
  const abs = Array.from(field.data, (v) => Math.abs(v));
  abs.sort((a, b) => a - b);
  const p98 = abs.length > 0 ? abs[Math.floor(0.98 * (abs.length - 1))]! : 0;
  return Math.max(0.1, p98);
}

/** Ramp max (m/s) for the wind-speed overlay: at least 5 m/s, or the observed max. */
export function windSpeedRampMax(field: ScalarField): number {
  let max = 0;
  for (const v of field.data) if (v > max) max = v;
  return Math.max(5, max);
}

export function windSpeedColor(speed: number, max: number): RGB {
  return sequentialColor(speed, max, WINDSPEED_STOPS);
}

/** Half-range (°C) for the diverging temperature scale, from the profile at the terrain's
 * elevation extremes (temperature is monotonic in elevation, so this bounds the actual range). */
export function temperatureHalfRange(conditions: Conditions, terrain: TerrainState): number {
  const tLow = temperatureAt(conditions.temperature, terrain.minElevation);
  const tHigh = temperatureAt(conditions.temperature, terrain.maxElevation);
  return Math.max(1, Math.abs(tLow), Math.abs(tHigh));
}

function temperatureColor(temp: number, half: number): RGB {
  // Square root so colour builds quickly away from 0 °C; linear leaves most of a typical
  // (−2…−12 °C) mountain pale. Keep the legend gradient in styles.css in sync.
  const t = Math.sqrt(clamp01(Math.abs(temp) / half));
  return temp >= 0 ? mix(TEMP_ZERO, TEMP_WARM, t) : mix(TEMP_ZERO, TEMP_COLD, t);
}

function phaseColor(frac: number): RGB {
  return frac >= 0.5
    ? mix(PHASE_MIXED, PHASE_SNOW, (frac - 0.5) * 2)
    : mix(PHASE_RAIN, PHASE_MIXED, frac * 2);
}

export interface OverlayLegend {
  mode: OverlayMode;
  snowDepthMax?: number;
  tempHalfRange?: number;
  zeroElevation?: number | null;
  twoDegElevation?: number | null;
  /** m, storm-snow ramp max. */
  stormSnowMaxM?: number;
  /** m, wind-loading diverging half-range. */
  windLoadingHalfM?: number;
  /** m/s, wind-speed ramp max. */
  windSpeedMax?: number;
  /** deg, shelter diverging half-range (fixed). */
  shelterHalfDeg?: number;
  /** cm, critical-depth ramp max. */
  criticalDepthMaxCm?: number;
  /** cm, avalanche deposit-depth ramp max. */
  avDepositMaxCm?: number;
  /** cm, avalanche net-change diverging half-range. */
  avNetHalfCm?: number;
  /** m/s, avalanche max-speed ramp max. */
  avMaxSpeedMax?: number;
  /** m, avalanche max-thickness ramp max. */
  avMaxThicknessMax?: number;
  /** Set instead of the numeric fields above when the overlay has no data to show yet. */
  note?: string;
}

const STORM_OVERLAY_MODES = new Set<OverlayMode>([
  'stormSnow',
  'windLoading',
  'windSpeed',
  'shelter',
]);

const STABILITY_OVERLAY_MODES = new Set<OverlayMode>([
  'stability',
  'naturalStability',
  'problem',
  'criticalDepth',
  'propagation',
]);

const AVALANCHE_OVERLAY_MODES = new Set<OverlayMode>([
  'avDeposit',
  'avNet',
  'avOrigin',
  'avMaxSpeed',
  'avMaxPressure',
  'avMaxThickness',
]);

/** Legend metadata for the current overlay — scale extents, reference elevations, etc.
 * `activeField` is the (already terrain-resampled) data for the storm overlays, or null when
 * unavailable; `hasStorm` distinguishes "still computing" from "no storm in the timeline" in
 * that case (weather is now a timeline of events rather than a single toggleable storm). */
export function overlayLegend(
  mode: OverlayMode,
  terrain: TerrainState,
  snowDepth: ScalarField,
  conditions: Conditions,
  activeField: ScalarField | null,
  hasStorm: boolean,
): OverlayLegend {
  if (STORM_OVERLAY_MODES.has(mode)) {
    if (!activeField) return { mode, note: hasStorm ? 'Computing…' : 'No storm in this timeline' };
    switch (mode) {
      case 'stormSnow':
        return { mode, stormSnowMaxM: stormSnowRampMax(activeField) };
      case 'windLoading':
        return { mode, windLoadingHalfM: windLoadingHalfRangeM(activeField) };
      case 'windSpeed':
        return { mode, windSpeedMax: windSpeedRampMax(activeField) };
      default:
        return { mode, shelterHalfDeg: SHELTER_HALF_RANGE_DEG };
    }
  }
  if (STABILITY_OVERLAY_MODES.has(mode)) {
    if (!activeField) return { mode, note: 'Computing…' };
    if (mode === 'criticalDepth') {
      return { mode, criticalDepthMaxCm: criticalDepthRampMax(activeField) * 100 };
    }
    return { mode };
  }
  if (AVALANCHE_OVERLAY_MODES.has(mode)) {
    if (!activeField) return { mode, note: 'No avalanche run yet' };
    switch (mode) {
      case 'avDeposit':
        return { mode, avDepositMaxCm: depositRampMax(activeField) * 100 };
      case 'avNet':
        return { mode, avNetHalfCm: netHalfRange(activeField) * 100 };
      case 'avMaxSpeed':
        return { mode, avMaxSpeedMax: speedRampMax(activeField) };
      case 'avMaxThickness':
        return { mode, avMaxThicknessMax: thicknessRampMax(activeField) };
      default:
        return { mode };
    }
  }
  if (mode === 'forest' && !activeField) return { mode, note: 'No forest data for this area' };
  switch (mode) {
    case 'snowDepth':
      return { mode, snowDepthMax: snowDepthRampMax(snowDepth) };
    case 'temperature':
      return { mode, tempHalfRange: temperatureHalfRange(conditions, terrain) };
    case 'precipPhase':
      return {
        mode,
        zeroElevation: elevationOfTemperature(conditions.temperature, 0),
        twoDegElevation: elevationOfTemperature(conditions.temperature, 2),
      };
    default:
      return { mode };
  }
}

/** Fill `out` (length = 3 × cell count) with the overlay's per-vertex colour. `activeField` is
 * the resampled storm data for the four storm overlays (see `overlayLegend`); ignored otherwise. */
export function computeColors(
  out: Float32Array,
  terrain: TerrainState,
  snowDepth: ScalarField,
  mode: OverlayMode,
  conditions: Conditions,
  activeField: ScalarField | null,
): void {
  const elev = terrain.elevation.data;
  const slope = terrain.topo.slope.data;
  const snow = snowDepth.data;
  const n = elev.length;

  const write = (i: number, rgb: RGB): void => {
    out[i * 3] = rgb[0];
    out[i * 3 + 1] = rgb[1];
    out[i * 3 + 2] = rgb[2];
  };

  switch (mode) {
    case 'natural':
      for (let i = 0; i < n; i++) write(i, naturalColor(elev[i]!, slope[i]!, snow[i]!));
      break;
    case 'slope':
      for (let i = 0; i < n; i++) write(i, slopeColor(slope[i]!));
      break;
    case 'snowDepth': {
      const max = snowDepthRampMax(snowDepth);
      for (let i = 0; i < n; i++) write(i, snowDepthColor(snow[i]!, max));
      break;
    }
    case 'temperature': {
      const half = temperatureHalfRange(conditions, terrain);
      for (let i = 0; i < n; i++)
        write(i, temperatureColor(temperatureAt(conditions.temperature, elev[i]!), half));
      break;
    }
    case 'precipPhase':
      for (let i = 0; i < n; i++) {
        write(i, phaseColor(snowFraction(temperatureAt(conditions.temperature, elev[i]!))));
      }
      break;
    case 'stormSnow': {
      if (!activeField) {
        for (let i = 0; i < n; i++) write(i, OVERLAY_NEUTRAL);
        break;
      }
      const max = stormSnowRampMax(activeField);
      for (let i = 0; i < n; i++)
        write(i, sequentialColor(Math.max(0, activeField.data[i]!), max, STORMSNOW_STOPS));
      break;
    }
    case 'windLoading': {
      if (!activeField) {
        for (let i = 0; i < n; i++) write(i, OVERLAY_NEUTRAL);
        break;
      }
      const half = windLoadingHalfRangeM(activeField);
      for (let i = 0; i < n; i++) write(i, divergingColor(activeField.data[i]!, half, SCOUR, LOAD));
      break;
    }
    case 'windSpeed': {
      if (!activeField) {
        for (let i = 0; i < n; i++) write(i, OVERLAY_NEUTRAL);
        break;
      }
      const max = windSpeedRampMax(activeField);
      for (let i = 0; i < n; i++) write(i, windSpeedColor(activeField.data[i]!, max));
      break;
    }
    case 'shelter': {
      if (!activeField) {
        for (let i = 0; i < n; i++) write(i, OVERLAY_NEUTRAL);
        break;
      }
      for (let i = 0; i < n; i++)
        write(i, divergingColor(activeField.data[i]!, SHELTER_HALF_RANGE_DEG, EXPOSED, SHELTERED));
      break;
    }
    case 'stability':
    case 'naturalStability': {
      if (!activeField) {
        for (let i = 0; i < n; i++) write(i, OVERLAY_NEUTRAL);
        break;
      }
      for (let i = 0; i < n; i++) {
        const v = activeField.data[i]!;
        write(
          i,
          Number.isNaN(v) || slope[i]! < STABILITY_NEUTRAL_SLOPE_DEG
            ? OVERLAY_NEUTRAL
            : stabilityClassColor(v),
        );
      }
      break;
    }
    case 'problem': {
      if (!activeField) {
        for (let i = 0; i < n; i++) write(i, OVERLAY_NEUTRAL);
        break;
      }
      for (let i = 0; i < n; i++) {
        const p = Math.round(activeField.data[i]!);
        write(i, p >= 1 && p <= PROBLEM_COLORS.length ? PROBLEM_COLORS[p - 1]! : OVERLAY_NEUTRAL);
      }
      break;
    }
    case 'criticalDepth': {
      if (!activeField) {
        for (let i = 0; i < n; i++) write(i, OVERLAY_NEUTRAL);
        break;
      }
      const max = criticalDepthRampMax(activeField);
      for (let i = 0; i < n; i++) {
        const v = activeField.data[i]!;
        write(i, Number.isNaN(v) ? OVERLAY_NEUTRAL : sequentialColor(v, max, CRITICALDEPTH_STOPS));
      }
      break;
    }
    case 'forest': {
      if (!activeField) {
        for (let i = 0; i < n; i++) write(i, OVERLAY_NEUTRAL);
        break;
      }
      for (let i = 0; i < n; i++) {
        const v = activeField.data[i]!;
        write(i, v >= 0.1 ? sequentialColor(v, 1, FOREST_STOPS) : OVERLAY_NEUTRAL);
      }
      break;
    }
    case 'propagation': {
      if (!activeField) {
        for (let i = 0; i < n; i++) write(i, OVERLAY_NEUTRAL);
        break;
      }
      for (let i = 0; i < n; i++) {
        const v = activeField.data[i]!;
        write(i, Number.isNaN(v) ? OVERLAY_NEUTRAL : sequentialColor(v, 1, PROPAGATION_STOPS));
      }
      break;
    }
    case 'avDeposit': {
      if (!activeField) {
        for (let i = 0; i < n; i++) write(i, OVERLAY_NEUTRAL);
        break;
      }
      const max = depositRampMax(activeField);
      for (let i = 0; i < n; i++) {
        const v = activeField.data[i]!;
        write(i, v > 0.001 ? depositColor(v, max) : OVERLAY_NEUTRAL);
      }
      break;
    }
    case 'avNet': {
      if (!activeField) {
        for (let i = 0; i < n; i++) write(i, OVERLAY_NEUTRAL);
        break;
      }
      const half = netHalfRange(activeField);
      for (let i = 0; i < n; i++) {
        const v = activeField.data[i]!;
        write(i, Math.abs(v) > 1e-4 ? netColor(v, half) : OVERLAY_NEUTRAL);
      }
      break;
    }
    case 'avOrigin': {
      if (!activeField) {
        for (let i = 0; i < n; i++) write(i, OVERLAY_NEUTRAL);
        break;
      }
      for (let i = 0; i < n; i++) {
        const v = activeField.data[i]!;
        write(i, Number.isNaN(v) ? OVERLAY_NEUTRAL : originColor(v));
      }
      break;
    }
    case 'avMaxSpeed': {
      if (!activeField) {
        for (let i = 0; i < n; i++) write(i, OVERLAY_NEUTRAL);
        break;
      }
      for (let i = 0; i < n; i++) {
        const v = activeField.data[i]!;
        write(i, v > 0.05 ? speedColor(v) : OVERLAY_NEUTRAL);
      }
      break;
    }
    case 'avMaxThickness': {
      if (!activeField) {
        for (let i = 0; i < n; i++) write(i, OVERLAY_NEUTRAL);
        break;
      }
      for (let i = 0; i < n; i++) {
        const v = activeField.data[i]!;
        write(i, v > 0.01 ? thicknessColor(v) : OVERLAY_NEUTRAL);
      }
      break;
    }
    case 'avMaxPressure': {
      if (!activeField) {
        for (let i = 0; i < n; i++) write(i, OVERLAY_NEUTRAL);
        break;
      }
      for (let i = 0; i < n; i++) {
        const v = activeField.data[i]!;
        write(i, v > 0.01 ? pressureColor(v) : OVERLAY_NEUTRAL);
      }
      break;
    }
  }
}
